/**
 * liveSessionListAdapter — maps `live_occurrences` rows → `UILiveSession`.
 *
 * API-aware (occurrence flags, substitution, makeup). Not imported by the DS.
 *
 * Remplaçant / Replanifié / Terminé live on `live_session.next_occurrences[]`, never on the
 * occurrence row — see `nextOccurrenceFor`.
 */
import moment from "moment"

import { formatHour } from "../../../ui/formatters/dateFormatter"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"
import { type UILiveSession, type UILiveSessionStatus, type UISubject, type UITutor } from "../../../ui/types"

export type ApiLiveOccurrenceTutor = {
    id?: string | number
    firstname?: string
    lastname?: string
    display_name?: string
    /** The only avatar key this payload carries (`UserLighterResource`), null without a photo. */
    avatar?: string
    is_tutor?: boolean
}

export type ApiAttendanceFlag = {
    id?: string | number
    type?: string
    user_id?: string | number
    date?: string
    recovery_live_occurrence_id?: string | number | null
}

/**
 * One row of `live_sessions[].next_occurrences[]` — the ONLY place the payload carries
 * substitution / rescheduling / finalization. It is not on the occurrence itself.
 */
export type ApiNextOccurrence = {
    date?: string
    substitution_id?: string | number | null
    substitute_id?: string | number | null
    rescheduling_id?: string | number | null
    finalized_at?: string | null
}

export type ApiLiveSessionNested = {
    id?: string | number
    duration?: number
    live_room_id?: string | number
    tutor?: ApiLiveOccurrenceTutor | null
    live_room?: { id?: string | number; type?: string } | null
    attendance_flags?: ApiAttendanceFlag[] | null
    school_subjects?: ApiSchoolSubject[] | null
    students?: ApiStudentForSubjects[] | null
    next_occurrences?: ApiNextOccurrence[] | null
}

export type ApiLiveOccurrence = {
    id?: string | number
    date?: string
    is_temporary?: boolean
    tutor?: ApiLiveOccurrenceTutor | null
    live_session?: ApiLiveSessionNested | null
}

/** Both payload sides serialize the date as "YYYY-MM-DD HH:mm" — normalized so a join can't drift. */
function occurrenceDateKey(date: string | null | undefined): string | null {
    if (!date) return null
    const parsed = moment(date)
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : date
}

/**
 * The `next_occurrences` row describing this occurrence, matched on `(live_session, date)`.
 * Same join as legacy `StudentLiveCardComponent` (`relevantOccurrence`), which reads it off the
 * live session nested in the occurrence — the occurrence itself carries none of these flags.
 */
function nextOccurrenceFor(
    liveSession: ApiLiveSessionNested | null | undefined,
    occurrenceDate: string | undefined
): ApiNextOccurrence | undefined {
    const key = occurrenceDateKey(occurrenceDate)
    if (key == null) return undefined
    return (liveSession?.next_occurrences ?? []).find((next) => occurrenceDateKey(next?.date) === key)
}

export type ApiPedagogicalObjective = {
    /** Subject IDs attached to the objective — see `PedagogicalObjectiveResource`. */
    subject_ids?: (string | number)[] | null
    status?: string | null
    live_sessions?: { id?: string | number }[] | null
}

export type ApiStudentForSubjects = {
    id?: string | number
    email?: string
    pedagogical_objectives?: ApiPedagogicalObjective[] | null
    is_payment_blocked?: boolean
}

export function toUITutor(raw: ApiLiveOccurrenceTutor | null | undefined): UITutor {
    if (raw == null) {
        return { id: 0, firstname: "", lastname: "" }
    }
    let firstname = raw.firstname ?? ""
    let lastname = raw.lastname ?? ""
    if (!firstname && raw.display_name) {
        const parts = raw.display_name.trim().split(/\s+/)
        firstname = parts[0] ?? ""
        lastname = parts.slice(1).join(" ")
    }
    return {
        id: raw.id ?? 0,
        firstname,
        lastname,
        avatarUrl: raw.avatar,
    }
}

/**
 * Subjects for a live card — mirrors legacy `ObjectiveSubjectsBadges` /
 * `resolveSessionObjective`: first non-closed pedagogical objective linked to
 * the session → `subject_ids` resolved against static school subjects.
 */
export function subjectsForSession(
    liveSessionId: string | number | undefined,
    student: ApiStudentForSubjects | null | undefined,
    apiSubjects: ApiSchoolSubject[],
    fallbackSubjects: ApiSchoolSubject[] | null | undefined
): UISubject[] {
    if (liveSessionId != null) {
        const objective = student?.pedagogical_objectives
            ?.filter((obj) => obj?.status !== "closed")
            ?.find(
                (obj) =>
                    Array.isArray(obj?.live_sessions) &&
                    obj.live_sessions.some((ls) => String(ls?.id) === String(liveSessionId))
            )

        const fromObjective =
            objective?.subject_ids
                ?.map((id) => apiSubjects.find((s) => String(s?.id) === String(id)))
                .filter((s): s is ApiSchoolSubject => s != null)
                .map(toUISubject) ?? []

        if (fromObjective.length > 0) return fromObjective
    }

    return (fallbackSubjects ?? []).map(toUISubject)
}

/** « Rattrapage de l'absence du 18/09/2026 à 17h00 » — l'heure comme l'espace parent l'écrit. */
function formatRecoveryInfo(absenceDate: string | undefined): string | undefined {
    if (!absenceDate) return undefined
    return `Rattrapage de l'absence du ${moment(absenceDate).format("DD/MM/YYYY")} à ${formatHour(absenceDate)}`
}

/**
 * Status chips — legacy stacks several badges; order mirrors `StudentLiveCardComponent`.
 * Remplaçant wins over Replanifié when both apply (legacy single badge).
 */
function resolveStatuses(args: {
    paymentBlocked?: boolean
    hasAbsenceFlag: boolean
    hasLateFlag: boolean
    isRecovery: boolean
    isTemporarilyAdded: boolean
    isHolidayCourse: boolean
    isReplacement: boolean
    isRescheduled: boolean
    isFinalized: boolean
}): UILiveSessionStatus[] {
    const {
        paymentBlocked,
        hasAbsenceFlag,
        hasLateFlag,
        isRecovery,
        isTemporarilyAdded,
        isHolidayCourse,
        isReplacement,
        isRescheduled,
        isFinalized,
    } = args

    const out: UILiveSessionStatus[] = []
    if (paymentBlocked && !hasAbsenceFlag) out.push("payment_blocked")
    if (hasAbsenceFlag) out.push("absent")
    if (hasLateFlag && !hasAbsenceFlag) out.push("late")
    if (!hasAbsenceFlag && !isHolidayCourse) {
        if (isRecovery) out.push("makeup_recovery")
        else if (isTemporarilyAdded) out.push("makeup_temporary")
    }
    if (isReplacement) out.push("substitute")
    else if (isRescheduled) out.push("rescheduled")
    if (isFinalized) out.push("finished")
    return out
}

export type ToUILiveSessionOptions = {
    student?: ApiStudentForSubjects | null
    apiSubjects?: ApiSchoolSubject[]
    paymentBlocked?: boolean
}

/**
 * L'absence que rattrape chaque occurrence : le drapeau dont `recovery_live_occurrence_id` la
 * désigne. Il vit sur la séance D'ORIGINE, celle de l'absence, jamais sur celle du rattrapage :
 * d'où une lecture de toute la liste, comme le legacy `StudentLiveCardComponent`
 * (`liveOccurrences.flatMap(…attendance_flags).find(…)` — le premier trouvé l'emporte).
 *
 * Une absence passée n'y est pas : `LiveSession::attendanceFlags` ne sert que les drapeaux datés
 * d'aujourd'hui ou après. Son rattrapage reste « makeup_temporary », dans le legacy aussi.
 */
function recoveryFlagsByOccurrence(
    occurrences: ApiLiveOccurrence[],
    userId: string | number | undefined
): Map<string, ApiAttendanceFlag> {
    const byOccurrence = new Map<string, ApiAttendanceFlag>()
    for (const flag of occurrences.flatMap((occ) => occ?.live_session?.attendance_flags ?? [])) {
        if (flag?.type !== "absent" || String(flag.user_id) !== String(userId)) continue
        if (flag.recovery_live_occurrence_id == null) continue
        const key = String(flag.recovery_live_occurrence_id)
        if (!byOccurrence.has(key)) byOccurrence.set(key, flag)
    }
    return byOccurrence
}

/** Une ligne. L'absence qu'elle rattrape arrive de `toUILiveSessions`, seul à voir toute la liste. */
function toUILiveSession(
    occurrence: ApiLiveOccurrence | null | undefined,
    options: ToUILiveSessionOptions,
    recoveryForAbsenceFlag: ApiAttendanceFlag | undefined
): UILiveSession | null {
    if (occurrence?.id == null || occurrence.date == null) return null

    const { student, apiSubjects = [], paymentBlocked = false } = options
    const liveSession = occurrence.live_session
    const userId = student?.id
    const occurrenceDate = occurrence.date
    const tutor = toUITutor(occurrence.tutor ?? liveSession?.tutor)

    const flags = liveSession?.attendance_flags ?? []
    const absenceFlags = flags.filter(
        (f) => f.type === "absent" && String(f.user_id) === String(userId) && f.date === occurrenceDate
    )
    const lateFlags = flags.filter(
        (f) => f.type === "late" && String(f.user_id) === String(userId) && f.date === occurrenceDate
    )
    const hasAbsenceFlag = absenceFlags.length > 0
    const hasLateFlag = lateFlags.length > 0

    const isRecovery = !!recoveryForAbsenceFlag
    const isTemporarilyAdded = !!occurrence.is_temporary
    const isHolidayCourse = liveSession?.live_room?.type === "holiday_course"
    const nextOccurrence = nextOccurrenceFor(liveSession, occurrenceDate)

    const statuses = resolveStatuses({
        paymentBlocked,
        hasAbsenceFlag,
        hasLateFlag,
        isRecovery,
        isTemporarilyAdded,
        isHolidayCourse,
        isReplacement: !!nextOccurrence?.substitution_id,
        isRescheduled: !!nextOccurrence?.rescheduling_id,
        isFinalized: !!nextOccurrence?.finalized_at,
    })

    const shouldShowRecoveryWithAbsenceDate = isRecovery && !!recoveryForAbsenceFlag && !hasAbsenceFlag

    const lateFlag = lateFlags[lateFlags.length - 1]
    const absenceFlag = absenceFlags[0]

    return {
        id: occurrence.id,
        date: occurrence.date,
        duration: liveSession?.duration ?? 0,
        tutor,
        subjects: subjectsForSession(liveSession?.id, student, apiSubjects, liveSession?.school_subjects),
        statuses,
        recoveryInfo: shouldShowRecoveryWithAbsenceDate ? formatRecoveryInfo(recoveryForAbsenceFlag?.date) : undefined,
        liveSessionId: liveSession?.id,
        liveRoomId: liveSession?.live_room_id ?? liveSession?.live_room?.id,
        roomType: liveSession?.live_room?.type,
        studentId: userId,
        absenceFlagId: absenceFlag?.id,
        lateFlagId: lateFlag?.id,
        recoveryFlagId: recoveryForAbsenceFlag?.id,
        recoverySourceDate: recoveryForAbsenceFlag?.date,
        recoveryOccurrenceId: absenceFlag?.recovery_live_occurrence_id ?? undefined,
    }
}

export function toUILiveSessions(
    occurrences: ApiLiveOccurrence[] | null | undefined,
    options: ToUILiveSessionOptions = {}
): UILiveSession[] {
    const list = occurrences ?? []
    const byId = new Map(list.filter((o) => o?.id != null).map((o) => [String(o.id), o]))
    const recoveredAbsences = recoveryFlagsByOccurrence(list, options.student?.id)

    return list
        .map((occ) => toUILiveSession(occ, options, recoveredAbsences.get(String(occ?.id))))
        .filter((s): s is UILiveSession => s != null)
        .map((session) => {
            if (session.recoveryOccurrenceId == null) return session
            const recoveryOcc = byId.get(String(session.recoveryOccurrenceId))
            return recoveryOcc?.date ? { ...session, recoveryOccurrenceDate: recoveryOcc.date } : session
        })
}
